import { ApiTrip, ApiFlights, ApiFlight, ApiFlightHop } from "../../service/travelerMapTripService";

/* ----------------- Transformed / UI model interfaces ----------------- */
export interface TransformedFlightItem {
  flightNumber: string;
  departureDate?: string;
  departureAirportCode?: string;
  arrivalAirportCode?: string;
  departureTime?: string | null;
  arrivalTime?: string | null;
  status: string;
  originalStatusMessage?: string; // Store original status message for time extraction
  hasStatusOverride?: boolean; // Flag to indicate if status was overridden by issues
  statusIndicator?: string;
  connection?: number | null;
  isCurrentFlight: boolean;
  hasHopIssues: boolean;
  hasConnectionIssues: boolean;
  flightHistoryId?: string | number; // Add flight history ID for connection matching
}

export interface TransformedCurrentFlight {
  departureAirportCode?: string;
  arrivalAirportCode?: string;
  departureCityState?: string;
  arrivalCityState?: string;
  scheduledDepartureTime?: string | null;
  actualDepartureTime?: string | null;
  scheduledArrivalTime?: string | null;
  actualArrivalTime?: string | null;
  flightNumber?: string;
}

export interface TransformedTrip {
  id: string;
  recordLocator?: string;
  passengerName?: string;
  groupName?: string;
  legCount: number;
  currentLegIndex: number;
  currentLegDepartureAirportCode?: string | null;
  currentLegArrivalAirportCode?: string | null;
  currentLegDepartureCityState?: string | null;
  currentLegArrivalCityState?: string | null;
  currentFlight?: TransformedCurrentFlight | null;
  flights: TransformedFlightItem[];
  tripIssues?: { type?: string; state?: string }[] | null;
}

/* ----------------- Helpers ----------------- */
function formatDateToDayMonYear(dateStr?: string | null): string | undefined {
  if (!dateStr) return undefined;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return undefined;
  const day = d.getDate().toString().padStart(2, "0");
  const mon = d.toLocaleString("en-US", { month: "short" });
  const year = d.getFullYear();
  return `${day}-${mon}-${year}`;
}

function formatTimeTo12Hour(dateStr?: string | null): string | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/* ----------------- Hop Issues Helper ----------------- */
function getLatestHopIssueType(hopIssues: any[]): string | null {
  if (!Array.isArray(hopIssues) || hopIssues.length === 0) {
    return null;
  }

  // Filter only open issues and sort by creation date (latest first)
  const openIssues = hopIssues
    .filter(issue => issue && issue.state === 'Open')
    .sort((a, b) => {
      const dateA = new Date(a.creationDate || 0).getTime();
      const dateB = new Date(b.creationDate || 0).getTime();
      return dateB - dateA; // Latest first
    });

  // Return the type of the latest open issue
  return openIssues.length > 0 ? openIssues[0].type : null;
}

/* ----------------- Main transformer ----------------- */
export function transformTrip(apiTrip: ApiTrip, pfhid: number, passengerName: string): TransformedTrip {
  const legs = Array.isArray(apiTrip.legs) ? apiTrip.legs : [];
  const legCount = legs.length;

  let currentLegIndex = -1;
  let currentFlightFound:
  | {
      legIndex: number;
      flightIndex: number;
      flight: ApiFlight;
      hopMatch: ApiFlightHop;
      allHops: ApiFlightHop[];
    }
  | null = null;

  // --- Identify current flight based on pfhid ---
  for (let li = 0; li < legs.length; li++) {
    const legFlights = Array.isArray(legs[li].flights) ? legs[li].flights : [];
    for (let fi = 0; fi < legFlights.length; fi++) {
      const flWrap = legFlights[fi];
      const flight = flWrap.flight;
      if (!flight) continue;

      const hops = Array.isArray(flWrap.hops) ? flWrap.hops : [];
      const hopMatch = hops.find((h) => h?.flightHistoryId === pfhid);

      if (hopMatch) {
        currentLegIndex = li;
        currentFlightFound = { legIndex: li, flightIndex: fi, flight, hopMatch, allHops: hops };
        break;
      }
    }
    if (currentLegIndex !== -1) break;
  }


  const currentLeg = currentLegIndex >= 0 ? legs[currentLegIndex] : undefined;

  const currentLegDepartureAirportCode = currentLeg?.departureAirportCode ?? null;
  const currentLegArrivalAirportCode = currentLeg?.arrivalAirportCode ?? null;
  
  // Get city state from first and last hops of current leg
  let currentLegDepartureCityState: string | null = null;
  let currentLegArrivalCityState: string | null = null;
  
  if (currentLeg && Array.isArray(currentLeg.flights)) {
    // Get departure city state from first hop of first flight
    const firstFlight = currentLeg.flights[0];
    if (firstFlight && Array.isArray(firstFlight.hops) && firstFlight.hops.length > 0) {
      const firstHop = firstFlight.hops[0];
      currentLegDepartureCityState = firstHop?.departureAirportCityState ?? null;
    }
    
    // Get arrival city state from last hop of last flight
    const lastFlight = currentLeg.flights[currentLeg.flights.length - 1];
    if (lastFlight && Array.isArray(lastFlight.hops) && lastFlight.hops.length > 0) {
      const lastHop = lastFlight.hops[lastFlight.hops.length - 1];
      currentLegArrivalCityState = lastHop?.arrivalAirportCityState ?? null;
    }
  }

  // --- Build currentFlight (pfhid matched) ---
  let currentFlight: TransformedCurrentFlight | null = null;
  if (currentFlightFound) {
    const flight = currentFlightFound.flight;
    const hops = Array.isArray(currentFlightFound?.allHops)
      ? currentFlightFound.allHops
      : Array.isArray(flight.hops)
      ? flight.hops
      : [];

    const departureHop = hops.find(
      (h) => h?.departureAirportCode === flight.departureAirportCode
    );
    const arrivalHop = hops.find(
      (h) => h?.arrivalAirportCode === flight.arrivalAirportCode
    );

    currentFlight = {
      departureAirportCode: flight.departureAirportCode,
      arrivalAirportCode: flight.arrivalAirportCode,
      departureCityState: departureHop?.departureAirportCityState,
      arrivalCityState: arrivalHop?.arrivalAirportCityState,
      scheduledDepartureTime: formatTimeTo12Hour(departureHop?.scheduledGateDepartureDateTime),
      actualDepartureTime: formatTimeTo12Hour(departureHop?.actualGateDepartureDateTime),
      scheduledArrivalTime: formatTimeTo12Hour(arrivalHop?.scheduledGateArrivalDateTime),
      actualArrivalTime: formatTimeTo12Hour(arrivalHop?.actualGateArrivalDateTime),
      flightNumber: `${flight.airline?.code ?? ""}${flight.number ?? ""}`,
    };
  }

  // --- Build flights for current leg ---
  const flightsOut: TransformedFlightItem[] = [];

  if (currentLeg && Array.isArray(currentLeg.flights)) {
    for (const flWrap of currentLeg.flights) {
      const flight = flWrap.flight;
      const hops = Array.isArray(flWrap.hops) ? flWrap.hops : [];

      // Process each hop as a separate flight entry
      for (let hopIndex = 0; hopIndex < hops.length; hopIndex++) {
        const hop = hops[hopIndex];
        if (!hop) continue;

        const isCurrentFlight = currentFlightFound?.hopMatch === hop;
        
        // Use the actual status from this specific hop
        const actualStatus = hop.statusMessage || "SCHEDULED";

        // Check flight-level or trip-level issues for this specific hop's pfhid
        let overriddenStatus: string | null = null;

        const allIssues = [
          ...(Array.isArray(flight.issues) ? flight.issues : []),
          ...(Array.isArray((apiTrip as any).issues) ? (apiTrip as any).issues : []),
        ];

        for (const iss of allIssues) {
          if (!iss) continue;
          if (
            (typeof iss.id !== "undefined" && String(iss.id) === String(hop.flightHistoryId)) ||
            (typeof iss.flightHistoryId !== "undefined" && iss.flightHistoryId === hop.flightHistoryId)
          ) {
            overriddenStatus = iss.type ?? null;
            break;
          }
        }

        // Check hop issues for this specific hop
        if (!overriddenStatus && hop && Array.isArray(hop.hopIssues) && hop.hopIssues.length > 0) {
          const latestHopIssueType = getLatestHopIssueType(hop.hopIssues);
          if (latestHopIssueType) {
            overriddenStatus = latestHopIssueType;
          }
        }

        // Check if this hop has issues for UI highlighting
        const hasHopIssues = !!(hop && Array.isArray(hop.hopIssues) && hop.hopIssues.length > 0);

        // Check if this hop has connection issues for UI highlighting
        const hasConnectionIssues = !!(hop && Array.isArray(hop.connectionIssues) && hop.connectionIssues.length > 0);

        // Get connection info (usually from the hop's connection property or flight's mct)
        let connection: number | null = null;
        if (hop.connection && typeof hop.connection.connectionTime === "number") {
          connection = hop.connection.connectionTime;
        } else if (typeof flight.mct === "number") {
          connection = flight.mct;
        }

        flightsOut.push({
          flightNumber: `${hop.airline?.code ?? flight.airline?.code ?? ""}${hop.number ?? flight.number ?? ""}`,
          departureDate: formatDateToDayMonYear(hop.actualGateDepartureDateTime ?? hop.scheduledGateDepartureDateTime ?? hop.departureTime ?? flight.departureTime ?? flight.utcDepartureTime),
          departureAirportCode: hop.departureAirportCode ?? flight.departureAirportCode,
          arrivalAirportCode: hop.arrivalAirportCode ?? flight.arrivalAirportCode,
          departureTime: formatTimeTo12Hour(hop.actualGateDepartureDateTime ?? hop.scheduledGateDepartureDateTime ?? hop.departureTime ?? flight.departureTime ?? flight.utcDepartureTime),
          arrivalTime: formatTimeTo12Hour(hop.actualGateArrivalDateTime ?? hop.scheduledGateArrivalDateTime ?? hop.arrivalTime ?? flight.arrivalTime ?? flight.utcArrivalTime),
          status: overriddenStatus ?? actualStatus,
          originalStatusMessage: actualStatus, // Store original for potential time extraction
          hasStatusOverride: !!overriddenStatus, // Flag if status was overridden
          statusIndicator: hop.statusIndicator,
          connection: connection,
          isCurrentFlight,
          hasHopIssues,
          hasConnectionIssues,
          flightHistoryId: hop.flightHistoryId, // Include flight history ID for connection matching
        });
      }
    }
  }

  // --- Trip-level issues mapping ---
  const tripIssuesMapped = Array.isArray((apiTrip as any).issues)
    ? (apiTrip as any).issues.map((i: any) => ({ type: i.type, state: i.state }))
    : null;

  // --- Final output ---
  return {
    id: apiTrip.id,
    recordLocator: apiTrip.recordLocator,
    groupName: apiTrip.groupName,
    passengerName,
    legCount,
    currentLegIndex,
    currentLegDepartureAirportCode,
    currentLegArrivalAirportCode,
    currentLegDepartureCityState,
    currentLegArrivalCityState,
    currentFlight,
    flights: flightsOut,
    tripIssues: tripIssuesMapped,
  };
}
